import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { track } from '../../../analytics/events'
import { ApiRequestError } from '../../../api/client'
import { useCreateOnboardingCsvRun } from '../../../api/hooks'
import type {
  OnboardingCsvError,
  OnboardingCsvInvalid,
  OnboardingCsvResult,
  OnboardingCsvRow,
  OnboardingRunScope,
} from '../../../api/onboardingTypes'
import {
  CSV_TEMPLATE,
  CsvEncodingError,
  parseOnboardingCsvInvalid,
  readCsvFile,
} from './bulkLogic'
import { INPUT_CLASS, PRIMARY_BUTTON_CLASS, SECONDARY_BUTTON_CLASS } from './ui'


// Bulk intake (plan B2): pick a CSV, Validate, read the table the SERVER
// parsed, then Start. The server re-parses on Start and never trusts this
// validation; Start is enabled only while the inputs are exactly what was last
// validated cleanly, so the table on screen is the run that gets created.

function inputKey(parts: (string | boolean | null)[]): string {
  return JSON.stringify(parts)
}

export default function BulkIntake({ onCreated }: { onCreated: (scopes: OnboardingRunScope[]) => void }) {
  const { t } = useTranslation('pages')
  const [reason, setReason] = useState('')
  const [csvText, setCsvText] = useState<string | null>(null)
  const [fileName, setFileName] = useState<string | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)
  const [acknowledged, setAcknowledged] = useState<Record<string, string>>({})
  const [overrideText, setOverrideText] = useState('')
  const [validated, setValidated] = useState<{ key: string; result: OnboardingCsvResult } | null>(
    null,
  )
  const [invalid, setInvalid] = useState<OnboardingCsvInvalid | null>(null)
  const [requestError, setRequestError] = useState<string | null>(null)
  const csvRun = useCreateOnboardingCsvRun()

  // Digits only: parseInt('5000000abc') is 5000000, and the override is the
  // operator typing the EXACT total — a trailing typo must not pass as consent.
  const overrideTrimmed = overrideText.trim()
  const overrideIsDigits = /^[0-9]+$/.test(overrideTrimmed)
  const override = overrideIsDigits ? Number(overrideTrimmed) : null
  const overrideValid =
    overrideTrimmed === '' || (override !== null && Number.isSafeInteger(override))
  const key = inputKey([csvText, overrideText, reason])
  const isValidated = validated !== null && validated.key === key

  const rows: OnboardingCsvRow[] = isValidated
    ? validated.result.rows
    : (invalid?.rows ?? [])
  const errorsByRecord = useMemo(() => {
    const map = new Map<number, OnboardingCsvError[]>()
    for (const error of invalid?.errors ?? []) {
      if (error.record === null) continue
      map.set(error.record, [...(map.get(error.record) ?? []), error])
    }
    return map
  }, [invalid])
  const fileErrors = (invalid?.errors ?? []).filter((error) => error.record === null)
  const requiresAck = rows.filter((row) => row.classification === 'requires_acknowledgement')
  const intakeReady = rows.every((row) => row.classification == null || row.classification === 'new' ||
    (row.classification === 'requires_acknowledgement' && row.history?.selected != null
      && acknowledged[row.email] === row.history.selected.target_id))
  const ceilingError = fileErrors.find((error) => error.column === 'credits')

  const handleFile = async (file: File | undefined) => {
    setValidated(null)
    setAcknowledged({})
    setInvalid(null)
    setFileError(null)
    setCsvText(null)
    setFileName(file?.name ?? null)
    if (!file) return
    try {
      setCsvText(await readCsvFile(file))
    } catch (error) {
      setFileError(
        error instanceof CsvEncodingError
          ? t('adminOnboarding.bulk.notUtf8')
          : t('adminOnboarding.bulk.unreadable'),
      )
    }
  }

  const handleTemplate = () => {
    const url = URL.createObjectURL(new Blob([CSV_TEMPLATE], { type: 'text/csv' }))
    const link = document.createElement('a')
    link.href = url
    link.download = 'onboarding-template.csv'
    link.click()
    URL.revokeObjectURL(url)
  }

  const submit = async (validateOnly: boolean) => {
    if (csvText === null || !overrideValid || (!validateOnly && (!isValidated || !intakeReady))) return
    setRequestError(null)
    const submittedKey = key
    try {
      const result = await csvRun.mutateAsync({
        reason: reason.trim() || 'validation',
        csvText,
        validateOnly,
        allowRepeatRecipients: false,
        acknowledgedPriorTargets: acknowledged,
        creditCeilingOverride: override,
      })
      setInvalid(null)
      if (validateOnly) {
        setValidated({ key: submittedKey, result })
        setAcknowledged({})
      } else {
        const scopes = result.scopes ?? (result.run ? [{ run_id: result.run.run_id, target_ids: result.run.targets.map((target) => target.target_id) }] : [])
        onCreated(scopes)
      }
    } catch (error) {
      setValidated(null)
      const parsed =
        error instanceof ApiRequestError ? parseOnboardingCsvInvalid(error.errorData) : null
      if (parsed) {
        setInvalid(parsed)
      } else {
        setInvalid(null)
        setRequestError(error instanceof Error ? error.message : String(error))
      }
    }
  }

  return (
    <div
      className="flex flex-col gap-4 rounded-xl border border-border bg-surface-1 p-5"
      data-testid="onboarding-bulk-intake"
    >
      <p className="text-xs text-muted-foreground">{t('adminOnboarding.bulk.intro')}</p>

      <div className="flex flex-wrap items-center gap-2">
        <label className={`${SECONDARY_BUTTON_CLASS} flex items-center`}>
          {t('adminOnboarding.bulk.chooseFile')}
          <input
            type="file"
            accept=".csv,text/csv"
            className="sr-only"
            onChange={(e) => void handleFile(e.target.files?.[0])}
            data-testid="onboarding-bulk-file"
          />
        </label>
        <span className="text-xs text-muted-foreground" data-testid="onboarding-bulk-file-name">
          {fileName ?? t('adminOnboarding.bulk.noFile')}
        </span>
        <button
          type="button"
          onClick={handleTemplate}
          className={`${SECONDARY_BUTTON_CLASS} ml-auto`}
          data-testid="onboarding-bulk-template"
        >
          {t('adminOnboarding.bulk.downloadTemplate')}
        </button>
      </div>
      {fileError && (
        <p className="text-xs text-destructive" data-testid="onboarding-bulk-file-error">
          {fileError}
        </p>
      )}

      <label className="flex flex-col gap-1.5">
        <span className="text-sm text-foreground">{t('adminOnboarding.reasonLabel')}</span>
        <input
          type="text"
          value={reason}
          onChange={(e) => { setReason(e.target.value); setAcknowledged({}); setValidated(null) }}
          placeholder={t('adminOnboarding.reasonPlaceholder')}
          className={INPUT_CLASS}
          data-testid="onboarding-bulk-reason"
        />
      </label>

      {rows.some((row) => row.classification === 'blocked_partial') && <p role="alert" className="text-sm text-destructive" data-testid="onboarding-bulk-partial-block">{rows.filter((row) => row.classification === 'blocked_partial').map((row) => row.email).join(', ')}: {t('adminOnboarding.history.partialBlocked')}</p>}
      {requiresAck.length > 0 && <div className="rounded-lg border border-warning/40 p-3 text-xs" data-testid="onboarding-bulk-conflicts">
        <p>{t('adminOnboarding.history.bulkAck')}</p>
        {requiresAck.map((row) => <label key={row.record} className="mt-2 flex gap-2" data-testid={`onboarding-bulk-ack-label-${row.record}`}>
          <input type="checkbox" data-testid={`onboarding-bulk-ack-${row.record}`} checked={acknowledged[row.email] === row.history?.selected?.target_id} onChange={(event) => {
            const prior = row.history?.selected?.target_id
            if (!prior) return
            setAcknowledged((previous) => { const next = { ...previous }; if (event.target.checked) next[row.email] = prior; else delete next[row.email]; return next })
            track('admin_onboarding_reonboard_acknowledgement_changed', { onboarding_mode: 'bulk', onboarding_acknowledged: event.target.checked, count: 1 })
          }} />{row.email} · {row.history?.selected?.completed_at} · {t('adminOnboarding.history.by', { actor: row.history?.selected?.actor_email })}
        </label>)}
      </div>}

      {(ceilingError || overrideText !== '') && (
        <label className="flex flex-col gap-1.5" data-testid="onboarding-bulk-ceiling">
          <span className="text-sm text-foreground">
            {t('adminOnboarding.bulk.ceilingOverrideLabel', {
              total: invalid?.total_credits ?? validated?.result.total_credits ?? 0,
            })}
          </span>
          <input
            type="text"
            inputMode="numeric"
            value={overrideText}
            onChange={(e) => { setOverrideText(e.target.value); setValidated(null); setAcknowledged({}) }}
            className={INPUT_CLASS}
            data-testid="onboarding-bulk-ceiling-input"
          />
        </label>
      )}

      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={csvText === null || !overrideValid || csvRun.isPending}
          onClick={() => void submit(true)}
          className={SECONDARY_BUTTON_CLASS}
          data-testid="onboarding-bulk-validate"
        >
          {t('adminOnboarding.bulk.validate')}
        </button>
        <button
          type="button"
          disabled={!isValidated || !intakeReady || reason.trim() === '' || csvRun.isPending}
          onClick={() => void submit(false)}
          className={PRIMARY_BUTTON_CLASS}
          data-testid="onboarding-bulk-start"
        >
          {isValidated
            ? t('adminOnboarding.bulk.start', {
                count: validated.result.rows.length,
                total: validated.result.total_credits.toLocaleString(),
              })
            : t('adminOnboarding.bulk.startDisabled')}
        </button>
      </div>

      {requestError && (
        <p className="text-xs text-destructive" data-testid="onboarding-bulk-request-error">
          {requestError}
        </p>
      )}

      {fileErrors.length > 0 && (
        <ul className="flex flex-col gap-1 text-xs text-destructive" data-testid="onboarding-bulk-file-errors">
          {fileErrors.map((error) => (
            <li key={error.message}>
              {error.lines
                ? `${t('adminOnboarding.bulk.linesLabel', { start: error.lines[0], end: error.lines[1] })}: `
                : ''}
              {error.message}
            </li>
          ))}
        </ul>
      )}

      {invalid && (
        <p className="text-xs text-destructive" data-testid="onboarding-bulk-error-count">
          {t('adminOnboarding.bulk.errorCount', { count: invalid.errors.length })}
        </p>
      )}

      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs" data-testid="onboarding-bulk-table">
            <thead className="text-muted-foreground">
              <tr>
                <th className="py-1 pr-2">#</th>
                <th className="py-1 pr-2">{t('adminOnboarding.history.decision')}</th>
                <th className="py-1 pr-2">{t('adminOnboarding.bulk.colEmail')}</th>
                <th className="py-1 pr-2">{t('adminOnboarding.bulk.colGreeting')}</th>
                <th className="py-1 pr-2 text-right">{t('adminOnboarding.bulk.colCredits')}</th>
                <th className="py-1 pr-2">{t('adminOnboarding.bulk.colOrg')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const rowErrors = errorsByRecord.get(row.record) ?? []
                return (
                  <tr
                    key={row.record}
                    className={rowErrors.length ? 'bg-destructive/10' : ''}
                    data-testid={`onboarding-bulk-row-${row.record}`}
                  >
                    <td className="py-1 pr-2 align-top text-muted-foreground">{row.record}</td>
                    <td className="py-1 pr-2 align-top text-foreground">{row.classification === 'blocked_partial' ? <p className="text-destructive">{t('adminOnboarding.history.partialBlocked')}</p> : row.classification === 'requires_acknowledgement' ? t('adminOnboarding.history.complete') : row.history?.account_exists ? t('adminOnboarding.history.untracked') : t('adminOnboarding.history.newRecipient')}
                    </td>
                    <td className="py-1 pr-2 align-top text-foreground">
                      {row.email}
                      {rowErrors.map((error) => (
                        <p key={error.message} className="text-destructive">
                          {error.message}
                        </p>
                      ))}
                    </td>
                    <td className="py-1 pr-2 align-top text-foreground">
                      {row.first_name ?? t('adminOnboarding.bulk.defaultGreeting')}
                    </td>
                    <td className="py-1 pr-2 text-right align-top text-foreground">
                      {row.credits.toLocaleString()}
                    </td>
                    <td className="py-1 pr-2 align-top text-foreground">{row.org_slug ?? '—'}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Parse errors on rows the server could not turn into a table row are
          listed by record and line, since there is no row to highlight. */}
      {invalid && errorsByRecord.size > 0 && rows.length === 0 && (
        <ul className="flex flex-col gap-1 text-xs text-destructive" data-testid="onboarding-bulk-row-errors">
          {[...errorsByRecord.entries()].flatMap(([record, errors]) =>
            errors.map((error) => (
              <li key={`${record}-${error.message}`}>
                {t('adminOnboarding.bulk.recordLabel', { record })}
                {error.lines
                  ? ` (${t('adminOnboarding.bulk.linesLabel', { start: error.lines[0], end: error.lines[1] })})`
                  : ''}
                {error.column ? ` · ${error.column}` : ''}: {error.message}
              </li>
            )),
          )}
        </ul>
      )}
    </div>
  )
}
